import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, Package, Search, Truck } from "lucide-react";
import { BentoCard, RydeFade, RydeFooter, RydeNav, SectionTag, WhatsAppButton } from "@/components/ryde/Ryde";
import { trackOrder } from "@/lib/api";
import { trackEvent } from "@/lib/tracking";

const TIMELINE = [
  ["queued", "Queue seat confirmed", "Your payment is verified and your queue ID is locked in."],
  ["packing", "Packing your unit", "Your Slate 01 is being packed with its serial number. Photo coming next."],
  ["dispatch_sent", "Dispatch confirmed", "Photo, serial number and tracking shared with you on WhatsApp. Balance is due now."],
  ["shipped", "On the road", "Your unit is with the courier."],
  ["delivered", "Delivered", "Kit in your hands. Warranty clock starts."],
];

export default function RydeTrack() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading || query.trim().length < 4) return;
    trackEvent("order_track_attempt", "", "/track");
    setLoading(true);
    setResult(null);
    try {
      const res = await trackOrder(query.trim());
      setResult(res);
      trackEvent("order_track_result", res.found ? "found" : "not_found", "/track");
    } catch {
      setResult({ found: false, error: true });
    } finally {
      setLoading(false);
    }
  };

  const activeIndex = result?.found
    ? Math.max(TIMELINE.findIndex(([key]) => key === result.order_status), 0)
    : -1;

  return (
    <main className="min-h-screen bg-[#0B0D0C] font-body text-[#F4F7F5]" data-testid="track-page">
      <RydeNav minimal />

      <section className="px-5 pb-24 pt-32 md:px-8 md:pt-40" data-track-section="track">
        <div className="mx-auto max-w-2xl">
          <RydeFade>
            <SectionTag testId="track-tag">Live order tracking</SectionTag>
            <h1 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Where’s my Slate 01?
            </h1>
            <p className="mt-5 text-lg leading-8 text-[#9AA8A0]">
              Enter the WhatsApp number you booked with, or your queue ID (like RC-0142).
            </p>

            <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="track-form">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="min-h-[56px] flex-1 rounded-xl border border-[#2A322E] bg-[#141816] px-5 text-base text-[#F4F7F5] outline-none transition-colors duration-200 placeholder:text-[#9AA8A0]/60 focus:border-[#C6FF3D] focus:ring-2 focus:ring-[#C6FF3D]/20"
                placeholder="10-digit number or RC-0142"
                data-testid="track-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl bg-[#C6FF3D] px-7 font-display text-base font-extrabold text-[#0B0D0C] transition-colors duration-200 hover:bg-[#D4FF6B] active:scale-95 disabled:opacity-60"
                data-testid="track-submit"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
                Track
              </button>
            </form>
          </RydeFade>

          <AnimatePresence>
            {result && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-8">
                {result.found ? (
                  <BentoCard data-testid="track-result">
                    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#2A322E] pb-5">
                      <div>
                        <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-[#9AA8A0]">Queue ID</p>
                        <p className="mt-1 font-jbmono text-2xl font-bold tracking-[0.12em] text-[#C6FF3D]" data-testid="track-queue-id">{result.queue_id || "—"}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-[#9AA8A0]">{result.first_name} · {result.city}</p>
                        <p className="mt-1 text-sm text-[#9AA8A0]">
                          Paid ₹{(result.amount_paid_inr || 0).toLocaleString("en-IN")}
                          {result.balance_inr > 0 && <span> · Balance ₹{result.balance_inr.toLocaleString("en-IN")} after dispatch</span>}
                        </p>
                      </div>
                    </div>
                    <div className="mt-6 space-y-0" data-testid="track-timeline">
                      {TIMELINE.map(([key, title, text], i) => {
                        const done = i <= activeIndex;
                        return (
                          <div key={key} className="flex gap-4" data-testid={`track-step-${key}`}>
                            <div className="flex flex-col items-center">
                              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${done ? "border-[#C6FF3D] bg-[#C6FF3D]/15 text-[#C6FF3D]" : "border-[#2A322E] text-[#9AA8A0]"}`}>
                                {done ? <CheckCircle2 size={15} /> : <span className="h-1.5 w-1.5 rounded-full bg-[#2A322E]" />}
                              </span>
                              {i < TIMELINE.length - 1 && <span className={`h-9 w-px ${i < activeIndex ? "bg-[#C6FF3D]/50" : "bg-[#2A322E]"}`} />}
                            </div>
                            <div className="pb-6">
                              <p className={`font-display text-base font-bold ${done ? "text-[#F4F7F5]" : "text-[#9AA8A0]"}`}>{title}</p>
                              <p className="mt-1 text-xs leading-5 text-[#9AA8A0]">{text}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </BentoCard>
                ) : (
                  <BentoCard data-testid="track-not-found">
                    <Package size={28} className="text-[#9AA8A0]" />
                    <p className="mt-4 font-display text-xl font-extrabold">No booking found for that.</p>
                    <p className="mt-2 text-sm leading-6 text-[#9AA8A0]">
                      Check the number or queue ID and try again. Only paid bookings appear here. Booked just now? Wait a minute and retry.
                    </p>
                    <WhatsAppButton source="track_not_found" message="I want to check my Slate 01 booking. City:" label="Ask on WhatsApp" testId="track-whatsapp" className="mt-6" />
                  </BentoCard>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <RydeFade delay={0.1}>
            <div className="mt-10 flex items-start gap-3 rounded-2xl border border-[#2A322E] bg-[#141816] p-5" data-testid="track-rule-note">
              <Truck className="mt-0.5 shrink-0 text-[#C6FF3D]" size={19} />
              <p className="text-sm leading-6 text-[#9AA8A0]">
                Every order is delivered within 15–20 days of booking. When your unit ships, your status here moves to “On the road” and we WhatsApp you the photo, serial, and tracking.
              </p>
            </div>
          </RydeFade>
        </div>
      </section>

      <RydeFooter />
    </main>
  );
}
